// Shared inbox triage filters — used by InboxView panels and legacy TriageBoard.
import { needsDurationReview } from '@/features/tasks/utils/data/durationReviewUtils';
import { SMALL_TASK_BUCKET_MINS } from '@/config/constants';
import {
  getTaskProjects,
  getTaskContexts,
} from '@/features/tasks/utils/data/taskTags';

export const DURATION_DROP_ZONES = [
  { id: 'triage-s', minutes: SMALL_TASK_BUCKET_MINS },
  { id: 'triage-duration-30', minutes: 30 },
  { id: 'triage-m', minutes: 60 },
  { id: 'triage-duration-120', minutes: 120 },
  { id: 'triage-l', minutes: 240 },
  { id: 'triage-duration-480', minutes: 480 },
];

const DURATION_BY_ZONE_ID = Object.fromEntries(
  DURATION_DROP_ZONES.map((z) => [z.id, z.minutes]),
);

export function getActiveParentIds(tasks) {
  const ids = new Set();
  tasks?.forEach((task) => {
    if (task.parentId && !task.completed) ids.add(task.parentId);
  });
  return ids;
}

export function getBaseInboxCandidates(tasks) {
  if (!tasks) return [];
  const activeParentIds = getActiveParentIds(tasks);
  return tasks.filter(
    (task) =>
      !task.completed &&
      !task.person &&
      task.status !== 'Backlog' &&
      !activeParentIds.has(task.id),
  );
}

export function sortByMaterializedPath(a, b) {
  const pathA = a.materializedPath || '';
  const pathB = b.materializedPath || '';
  if (pathA < pathB) return -1;
  if (pathA > pathB) return 1;
  return 0;
}

export function getDurationTriageTasks(tasks) {
  return getBaseInboxCandidates(tasks)
    .filter((task) => needsDurationReview(task))
    .sort(sortByMaterializedPath);
}

export function getUntaggedProjectTasks(tasks) {
  return getBaseInboxCandidates(tasks)
    .filter((task) => getTaskProjects(task).length === 0 && !task.parentId)
    .sort(sortByMaterializedPath);
}

export function getUntaggedContextTasks(tasks) {
  return getBaseInboxCandidates(tasks)
    .filter((task) => getTaskContexts(task).length === 0)
    .sort(sortByMaterializedPath);
}

export function getInboxTabCount(tasks) {
  const ids = new Set();
  getDurationTriageTasks(tasks).forEach((t) => ids.add(t.id));
  getUntaggedProjectTasks(tasks).forEach((t) => ids.add(t.id));
  getUntaggedContextTasks(tasks).forEach((t) => ids.add(t.id));
  return ids.size;
}

/** Flat inbox list — same deduplicated set as getInboxTabCount, sorted by path. */
export function getInboxTasks(tasks) {
  const seen = new Set();
  const merged = [];
  for (const task of [
    ...getDurationTriageTasks(tasks),
    ...getUntaggedProjectTasks(tasks),
    ...getUntaggedContextTasks(tasks),
  ]) {
    if (!seen.has(task.id)) {
      seen.add(task.id);
      merged.push(task);
    }
  }
  return merged.sort(sortByMaterializedPath);
}
export function getDurationMinutesForZone(overId) {
  return DURATION_BY_ZONE_ID[overId] ?? null;
}

export function buildProjectDropZones(tagMap) {
  return [...tagMap.entries()].map(([tagId, tagName]) => ({
    id: `triage-project-${tagId}`,
    label: tagName,
  }));
}

export function buildContextDropZones(tagMap) {
  return [...tagMap.entries()].map(([tagId, tagName]) => {
    const label = String(tagName).startsWith('@') ? tagName : `@${tagName}`;
    return { id: `triage-context-${tagId}`, label };
  });
}

const NEW_TAG_ID_PATTERN = /^[\w-]+$/;

/** Same normalization as taskParser tags: optional prefix char, lowercase id, preserve spelling as name. */
export function normalizeNewTagInput(raw, prefixChar) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  const withoutPrefix = trimmed.startsWith(prefixChar)
    ? trimmed.slice(1)
    : trimmed;
  const id = withoutPrefix.toLowerCase();
  if (!NEW_TAG_ID_PATTERN.test(id)) return null;
  return { id, name: withoutPrefix };
}

/** @param {string} raw — optional `@` prefix */
export function normalizeNewContextInput(raw) {
  return normalizeNewTagInput(raw, '@');
}

/** @param {string} raw — optional `#` prefix */
export function normalizeNewProjectInput(raw) {
  return normalizeNewTagInput(raw, '#');
}

/**
 * @param {Map<string, string>} tagMap
 * @param {Map<string, string>} [pendingNames] — id → display name for inbox-only zones
 */
export function buildContextDropZonesWithPending(tagMap, pendingNames = new Map()) {
  const zones = buildContextDropZones(tagMap);
  const seen = new Set(
    [...tagMap.keys()].map((key) => String(key).toLowerCase()),
  );
  for (const [pendingId, pendingName] of pendingNames) {
    const id = String(pendingId).toLowerCase();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const label = String(pendingName).startsWith('@')
      ? pendingName
      : `@${pendingName}`;
    zones.push({ id: `triage-context-${id}`, label });
  }
  return zones;
}

export function buildProjectDropZonesWithPending(tagMap, pendingNames = new Map()) {
  const zones = buildProjectDropZones(tagMap);
  const seen = new Set(
    [...tagMap.keys()].map((key) => String(key).toLowerCase()),
  );
  for (const [pendingId, pendingName] of pendingNames) {
    const id = String(pendingId).toLowerCase();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const label = String(pendingName).startsWith('#')
      ? pendingName
      : `#${pendingName}`;
    zones.push({ id: `triage-project-${id}`, label });
  }
  return zones;
}

export function buildDurationZoneLabels(_t, formatHours) {
  return DURATION_DROP_ZONES.map((zone) => ({
    ...zone,
    label: formatHours(zone.minutes / 60),
  }));
}
